import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { fetcher } from 'util/Csrf'
import { onError } from 'apollo-link-error'
import EnvVars from 'util/EnvVars'
import introspectionQueryResultData, {
  AppendErrorDocument,
  AppendErrorMutationVariables,
  ErrorsDocument,
  OpenCreateModalMutationVariables,
} from 'graphql/bindings/AdminBindings'

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
})

const cache = new InMemoryCache({
  fragmentMatcher,
  cacheRedirects: {
    Query: {
      deal: (_, { id }, { getCacheKey }) => {
        return getCacheKey({ id, __typename: 'DealView' })
      },
    },
  },
})

let client: ApolloClient<any> | null = null

// These GraphQL operations ignore network errors since they use pollInterval
// regularly against the backend.
//
// TODO: not sure how to do this in a cleaner fashion. It's basically what the Apollo docs recommend though.
const ignoredOperations = ['allTodos', 'ActiveDeals']
const link = ApolloLink.from([
  onError(({ operation, graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, path, locations }) => {
        client &&
          client.mutate({ mutation: AppendErrorDocument, variables: { errorMsg: `[GraphQL error: ${message}` } })
        console.error(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`)
      })

    if (networkError) {
      if (!ignoredOperations.find(item => item === operation.operationName)) {
        client &&
          client.mutate({ mutation: AppendErrorDocument, variables: { errorMsg: `[Network error: ${networkError}` } })
      }

      console.error(`[Network error]: ${networkError}`)
    }
  }),
  new BatchHttpLink({
    uri: `${EnvVars.API_URL}/admin`,
    credentials: 'include',
    batchMax: 100,
    batchInterval: 30,
    fetch: fetcher,
  }),
])

client = new ApolloClient({
  link: link,
  cache: cache,
  resolvers: {
    Mutation: {
      openCreateModal: (_root, variables: OpenCreateModalMutationVariables, { cache }) => {
        const data = {
          is_create_modal_open: true,
          create_modal_data: { ...variables.data, __typename: 'CreateModalData' },
        }
        cache.writeData({ data })
        return null
      },
      closeCreateModal: (_root, _variables, { cache }) => {
        cache.writeData({ data: { is_create_modal_open: false } })
        return null
      },
      appendError: (_root, variables: AppendErrorMutationVariables, { cache }) => {
        const msg = variables.errorMsg
        const { errors } = cache.readQuery({ query: ErrorsDocument })
        const newErrors = errors ? (errors as string[]).concat(msg) : [msg]
        cache.writeData({ data: { errors: newErrors } })
        return null
      },
      clearErrors: (_root, _variables, { cache }) => {
        cache.writeData({ data: { errors: [] } })
        return null
      },
    },
  },
})

client.cache.writeData({
  data: {
    is_create_opportunity_open: false,
    create_opportunity_account_id: '',
    create_opportunity_name: '',
    errors: [],
  },
})

export const AdminClient = client!
